import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { RequirementsService } from '../../../services/requirements.service';
import { RequirementActivity, RequirementActivityType } from '../../../models/requirement-activity.model';
import { RequirementActivityItem, toRequirementActivityItem } from './requirement-activity-item';
import { AvatarComponent } from '../../../components/avatar/avatar.component';
import { saveBlob } from '../../../shared/file-download';
import { findRouteParam } from '../../../shared/route-params';
import { ProjectRoleService } from '../../../services/project-role.service';
import { ProjektArchiviertHinweisComponent } from '../../../components/projekt-archiviert-hinweis/projekt-archiviert-hinweis.component';

/** Ein Filter-Chip: bündelt einen oder mehrere Activity-Typen unter einem Label. */
interface HistoryFilter {
  id: string;
  labelKey: string;
  types: RequirementActivityType[] | null; // null = alle
}

const FILTERS: HistoryFilter[] = [
  { id: 'all', labelKey: 'requirements.history.filters.all', types: null },
  { id: 'edit', labelKey: 'requirements.history.filters.changes', types: ['EDITED'] },
  { id: 'status', labelKey: 'requirements.history.filters.status', types: ['STATUS_CHANGED'] },
  {
    id: 'criteria',
    labelKey: 'requirements.history.filters.criteria',
    types: ['CRITERIA_ADDED', 'CRITERIA_UPDATED', 'CRITERIA_REMOVED'],
  },
  { id: 'comment', labelKey: 'requirements.history.filters.comments', types: ['COMMENT_ADDED'] },
];

/**
 * Audit-Trail-Seite einer Anforderung (NEX-492/NEX-494). Listet alle Ereignisse
 * chronologisch absteigend, filterbar per Chip, mit CSV-Export. Design-Referenz:
 * docs/design/v2026-10-02-bd42786a4dd/page-flows.jsx (PageHistorie).
 */
@Component({
  selector: 'app-requirement-history',
  imports: [DatePipe, MatIcon, MatProgressSpinner, TranslatePipe, AvatarComponent, ProjektArchiviertHinweisComponent],
  templateUrl: './requirement-history.component.html',
  styleUrl: './requirement-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequirementHistoryComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly requirementsService = inject(RequirementsService);
  private readonly projectRole = inject(ProjectRoleService, { optional: true });
  protected readonly isArchived = computed(() => this.projectRole?.isArchived() ?? false);

  readonly filters = FILTERS;
  readonly activeFilter = signal<string>('all');
  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly exporting = signal(false);
  /** true, wenn das BE weitere Seiten meldet (Liste auf {@link ACTIVITY_PAGE_SIZE} gekürzt). */
  readonly truncated = signal(false);
  private readonly activities = signal<RequirementActivity[]>([]);

  private projectId = '';
  private requirementId = '';
  /** Anzeige-Schlüssel im Kopf (extId/Jira-Key, sonst die Anforderungs-ID). */
  requirementKey = '';

  readonly totalCount = computed(() => this.activities().length);

  readonly counts = computed<Record<string, number>>(() => {
    const list = this.activities();
    const result: Record<string, number> = {};
    for (const f of this.filters) {
      result[f.id] = f.types === null ? list.length : list.filter((a) => f.types!.includes(a.type)).length;
    }
    return result;
  });

  readonly visibleItems = computed<RequirementActivityItem[]>(() => {
    const active = this.filters.find((f) => f.id === this.activeFilter());
    const types = active?.types ?? null;
    return this.activities()
      .filter((a) => types === null || types.includes(a.type))
      .map(toRequirementActivityItem);
  });

  ngOnInit(): void {
    this.projectId = this.findParam('projectId');
    this.requirementId = this.findParam('id');
    this.requirementKey = this.requirementId;
    this.loadRequirementKey();
    this.load();
  }

  /** Kopf-Schlüssel auf extId/Titel heben, wenn die Anforderung geladen werden kann. */
  private loadRequirementKey(): void {
    if (!this.projectId || !this.requirementId) {
      return;
    }
    this.requirementsService
      .getById(this.projectId, this.requirementId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => (this.requirementKey = r.extId || r.title || this.requirementId),
        error: () => {
          /* Kopf-Schlüssel bleibt die ID — kein blockierender Fehler. */
        },
      });
  }

  setFilter(id: string): void {
    this.activeFilter.set(id);
  }

  private load(): void {
    if (!this.projectId || !this.requirementId) {
      return;
    }
    this.loading.set(true);
    this.loadError.set(false);
    this.requirementsService
      .listActivities(this.projectId, this.requirementId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          this.activities.set(page.content);
          this.truncated.set(!page.last);
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  exportCsv(): void {
    if (!this.projectId || !this.requirementId) {
      return;
    }
    this.exporting.set(true);
    const active = this.filters.find((f) => f.id === this.activeFilter());
    const types = active?.types ?? undefined;
    this.requirementsService
      .exportActivitiesCsv(this.projectId, this.requirementId, types ?? undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          saveBlob(blob, `anforderung-${this.requirementId}-historie.csv`);
          this.exporting.set(false);
        },
        error: () => this.exporting.set(false),
      });
  }

  /** CSS-Modifier-Suffix für Farbe/Icon je Ereignis-Typ (siehe Design typeColors). */
  variant(type: RequirementActivityType): string {
    switch (type) {
      case 'CREATED':
        return 'create';
      case 'LINK_ADDED':
      case 'LINK_REMOVED':
        return 'link';
      case 'CRITERIA_ADDED':
      case 'CRITERIA_UPDATED':
      case 'CRITERIA_REMOVED':
        return 'criteria';
      case 'COMMENT_ADDED':
        return 'comment';
      case 'STATUS_CHANGED':
        return 'status';
      case 'DELETED':
        return 'delete';
      case 'HANDED_OVER':
      case 'HANDOVER_CLOSED':
      case 'HANDOVER_REOPENED':
        return 'handover';
      default:
        return 'edit';
    }
  }

  icon(type: RequirementActivityType): string {
    switch (this.variant(type)) {
      case 'create':
        return 'add';
      case 'link':
        return 'link';
      case 'criteria':
        return 'check';
      case 'comment':
        return 'chat_bubble_outline';
      case 'status':
        return 'flag';
      case 'delete':
        return 'delete_outline';
      case 'handover':
        return 'swap_horiz';
      default:
        return 'edit';
    }
  }

  private findParam(name: string): string {
    return findRouteParam(this.route, name) ?? '';
  }
}
